
import { useState } from 'react';
import { useBoardContext } from '@/contexts/BoardContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Trash2, Save } from 'lucide-react';
import type { Tables } from '@/integrations/supabase/types';

interface BoardGeneralTabProps {
  board: Tables<'boards'>;
}

export const BoardGeneralTab = ({ board }: BoardGeneralTabProps) => {
  const { updateBoard } = useBoardContext();
  const [title, setTitle] = useState(board.title);
  const [description, setDescription] = useState(board.description || '');
  const [isPublic, setIsPublic] = useState(board.is_public || false);
  const [defaultRole, setDefaultRole] = useState<'owner' | 'admin' | 'editor' | 'viewer'>(board.default_member_role || 'viewer');

  const handleSave = () => {
    updateBoard({
      title,
      description: description || null,
      is_public: isPublic,
      default_member_role: defaultRole
    });
  };

  const hasChanges = title !== board.title || 
                    description !== (board.description || '') ||
                    isPublic !== (board.is_public || false) ||
                    defaultRole !== (board.default_member_role || 'viewer');

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Board Information</CardTitle>
          <CardDescription>
            Update your board's basic information and settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Board Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter board title"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter board description (optional)"
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="public"
              checked={isPublic}
              onCheckedChange={setIsPublic}
            />
            <Label htmlFor="public">Make board public</Label>
          </div>

          <div>
            <Label htmlFor="defaultRole">Default member role</Label>
            <Select value={defaultRole} onValueChange={(value: 'owner' | 'admin' | 'editor' | 'viewer') => setDefaultRole(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {hasChanges && (
            <Button onClick={handleSave} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              Save Changes
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="border-red-200 dark:border-red-800">
        <CardHeader>
          <CardTitle className="text-red-600 dark:text-red-400">Danger Zone</CardTitle>
          <CardDescription>
            Irreversible and destructive actions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                Delete Board
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the board
                  and remove all associated data including columns, cards, and member access.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    // TODO: Implement board deletion
                    console.log('Delete board:', board.id);
                  }}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete Board
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
};
